import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import leaveService from '../../services/leaveService';

export const fetchLeaves = createAsyncThunk(
  'leaves/fetchLeaves',
  async ({ page = 1, limit = 10, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await leaveService.getAll(page, limit, filters);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch leaves');
    }
  }
);

export const applyLeave = createAsyncThunk(
  'leaves/applyLeave',
  async (leaveData, { rejectWithValue }) => {
    try {
      const response = await leaveService.apply(leaveData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to apply leave');
    }
  }
);

export const approveLeave = createAsyncThunk(
  'leaves/approveLeave',
  async ({ id, comments }, { rejectWithValue }) => {
    try {
      const response = await leaveService.approve(id, comments);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to approve leave');
    }
  }
);

export const rejectLeave = createAsyncThunk(
  'leaves/rejectLeave',
  async ({ id, comments }, { rejectWithValue }) => {
    try {
      const response = await leaveService.reject(id, comments);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reject leave');
    }
  }
);

const initialState = {
  leaves: [],
  pagination: {
    page: 1,
    limit: 10,
    total: 0
  },
  isLoading: false,
  error: null
};

const leaveSlice = createSlice({
  name: 'leaves',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch leaves
    builder
      .addCase(fetchLeaves.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLeaves.fulfilled, (state, action) => {
        state.isLoading = false;
        state.leaves = action.payload?.data || [];
        state.pagination = action.payload?.pagination || initialState.pagination;
      })
      .addCase(fetchLeaves.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Apply leave
    builder
      .addCase(applyLeave.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(applyLeave.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload?.data) {
          state.leaves.unshift(action.payload.data);
        }
      })
      .addCase(applyLeave.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Approve leave
    builder
      .addCase(approveLeave.fulfilled, (state, action) => {
        const leave = action.payload?.data;
        const index = state.leaves.findIndex((item) => item.id === leave?.id);
        if (index !== -1) {
          state.leaves[index] = leave;
        }
      });

    // Reject leave
    builder
      .addCase(rejectLeave.fulfilled, (state, action) => {
        const leave = action.payload?.data;
        const index = state.leaves.findIndex((item) => item.id === leave?.id);
        if (index !== -1) {
          state.leaves[index] = leave;
        }
      });
  }
});

export const { clearError } = leaveSlice.actions;
export default leaveSlice.reducer;
